import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import QRCode from 'qrcode';
import {
  addWithDecimals,
  divideWithDecimals,
  minusWithDecimals,
  multiplyWithDecimals,
} from '../../config/utils/calcUtils';
import { getLocalDate } from '../../config/utils/dateHelpers';
import {
  DOC_TYPE_AADE_CODE,
  DOC_TYPE_AADE_CODE_VALUES,
  LIST_OF_AADE_IDS_OF_RETURN_DOC_TYPES,
} from '../../interfaces/documentType.interface';
import { InvoiceInterface } from '../../interfaces/invoice.interface';
import { PAYMENT_METHOD_AADE_CODE, UNVERIFIED_PAYMENTS } from '../../interfaces/paymentTypes.interface';
import { ProductInOrder } from '../../interfaces/product.interface';
import { isDocTypeDeliveryNote } from './myDataUtils';
import { getPayableAmountOfProducts } from './productCost';

const MAX_PRODUCTS_IN_ONE_PAGE = 16;
const MAX_PRODUCTS_IN_LAST_PAGE = 8;

const PRODUCT_TITLE_HEIGHT = 15 + 4;
const PRODUCT_DESCRIPTION_HEIGHT = 15;
const PRODUCT_EXCEPTION_HEIGHT = 15;
const PRODUCT_DESCRIPTION_EXCEPTION_HEIGHT =
  PRODUCT_TITLE_HEIGHT + PRODUCT_DESCRIPTION_HEIGHT + PRODUCT_EXCEPTION_HEIGHT;

const MAX_HEIGHT_OF_PRODS_IN_PAGE = MAX_PRODUCTS_IN_ONE_PAGE * PRODUCT_DESCRIPTION_EXCEPTION_HEIGHT;
const MAX_HEIGHT_OF_PRODS_IN_LAST_PAGE = MAX_PRODUCTS_IN_LAST_PAGE * PRODUCT_DESCRIPTION_EXCEPTION_HEIGHT;

// console.log('MAX_HEIGHT_OF_PRODS_IN_PAGE: ', MAX_HEIGHT_OF_PRODS_IN_PAGE);
// console.log('MAX_HEIGHT_OF_PRODS_IN_LAST_PAGE: ', MAX_HEIGHT_OF_PRODS_IN_LAST_PAGE);

export const splitToPagesDynamic = (products: ProductInOrder[]) => {
  const splitted = [];
  let currentHeight = 0;
  let firstProdInPage = 0;

  for (let i = 0; i < products.length; i++) {
    const heightOfProd = getHeightOfProd(products[i]);

    // Prods fit to one page
    if (i + 1 === products.length) {
      if (currentHeight + heightOfProd <= MAX_HEIGHT_OF_PRODS_IN_LAST_PAGE) {
        currentHeight += heightOfProd;
      } else {
        splitted.push(products.slice(firstProdInPage, i));
        firstProdInPage = i;
        splitted.push(products.slice(firstProdInPage, i + 1));
        currentHeight = 0;
        continue;
      }
    }

    // Prods don't fit in last page but fit in one page
    if (currentHeight + heightOfProd <= MAX_HEIGHT_OF_PRODS_IN_PAGE) {
      currentHeight += heightOfProd;
    }

    // We need to push prods to page and change page
    if (currentHeight + heightOfProd > MAX_HEIGHT_OF_PRODS_IN_PAGE) {
      // Don't include current product in page since it doesn't fit
      splitted.push(products.slice(firstProdInPage, i));
      firstProdInPage = i;
      currentHeight = 0;
    }

    // We are in the last page
    if (i + 1 === products.length) {
      splitted.push(products.slice(firstProdInPage, i + 1));
    }
  }

  return splitted;
};

const getHeightOfProd = (product: ProductInOrder): number => {
  let heightOfProd = PRODUCT_TITLE_HEIGHT;
  if (product.description?.length) heightOfProd += PRODUCT_DESCRIPTION_HEIGHT;
  if (product.vatExceptionCategoryId) heightOfProd += PRODUCT_EXCEPTION_HEIGHT;

  return heightOfProd + 2;
};

export const getDateOfDoc = (docDate: string, sentToAadeDate: string | null) => {
  if (sentToAadeDate) {
    const postedSameDay = dayjs(sentToAadeDate).isSame(docDate, 'day');
    if (postedSameDay) {
      return dayjs(sentToAadeDate).format('DD/MM/YYYY - HH:mm');
    }
  }

  return getLocalDate(docDate);
};

export const getProdIndexInDoc = (products: ProductInOrder[], product: ProductInOrder) => {
  const findIndex = products?.findIndex((prod) => prod.id === product.id);

  return findIndex || 0;
};

export const showProductsCopyright = (doc: InvoiceInterface) => {
  const types = [
    DOC_TYPE_AADE_CODE.INVOICE,
    DOC_TYPE_AADE_CODE.INVOICE_INTRA_COMMUNITY_DELIVERIES,
    DOC_TYPE_AADE_CODE.INVOICE_THIRD_COUNTRIES_DELIVERIES,
    DOC_TYPE_AADE_CODE.DELIVERY_NOTE,
    DOC_TYPE_AADE_CODE.INVERTED_DELIVERY_NOTE,
  ] as DOC_TYPE_AADE_CODE_VALUES[];

  return types.includes(doc.documentType?.aadeCode);
};

export const getQRDataUrl = async (text: string) => {
  try {
    return await QRCode.toDataURL(text, { margin: 0 });
  } catch {
    // Should not fail ever
  }
};

// TODO: add extra type of docs here like (Titlos ktisis) to calculate correctly the new subtotal
export const getCustomerNewSubtotal = (doc: InvoiceInterface) => {
  const totalWithVat = new Decimal(getPayableAmountOfProducts(doc.products));

  // Check for possible tax withhold and use for new subtotal
  const taxWithholdPercentage = divideWithDecimals(doc.taxWithholdPercentage || '0', 100);
  const taxWithholdAmount = multiplyWithDecimals(taxWithholdPercentage, doc.totalValue);
  const totalToPay = totalWithVat.minus(new Decimal(taxWithholdAmount)).toFixed(2);

  // If I set customer subtotal in BE when POS with the new one (non zero)
  // then remove next if statement

  /**
   * if pos was successful => Return `currentSubtotal` since the payment was successful
   * else => as usual, return `currentSubtotal + totalToPay`
   */
  if (doc?.paymentType?.aadeCode === PAYMENT_METHOD_AADE_CODE.POS && doc?.mark) {
    return doc.currentSubtotal || 0;
  }

  if (!UNVERIFIED_PAYMENTS.includes(doc.paymentType?.aadeCode) || isDocTypeDeliveryNote(doc.documentType?.aadeCode)) {
    return doc?.currentSubtotal || 0;
  }

  if (LIST_OF_AADE_IDS_OF_RETURN_DOC_TYPES.includes(doc?.documentType?.aadeCode)) {
    return minusWithDecimals(doc.currentSubtotal || 0, totalToPay);
  }
  return addWithDecimals(doc.currentSubtotal || 0, totalToPay);
};
